import { AfterViewInit, Component, ElementRef, Inject, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { Subscription } from 'rxjs';
import { ChatbotService, InputService, SabisChatbotFloatingButtonComponent, ChatbotPopupService, ChatbotConfig, IMessageService, PageLinks } from 'sabis-chatbot';
import { CustomResizeDirective } from '../../directives/custom-resize.directive';
import { ChatbotApiService } from '../../services/sabis-chatbot/chatbot-api.service';
import { ChatbotWindowSizeService } from '../../services/sabis-chatbot/chatbot-window-size.service';
import { UserSessionService } from '../../services/user-session/user-session.service';
import { ChatbotTextTransferService } from '../../services/sabis-chatbot/chatbot-text-transfer.service';
import { Router } from '@angular/router';
import { AppRoutes } from '../../Constants/app-routes';
import { AppConfigService } from '../../services/app-config/app-config.service';


@Component({
    selector: 'app-sabis-chatbot',
    templateUrl: './sabis-chatbot.component.html',
    styleUrls: ['./sabis-chatbot.component.scss'],
    imports: [SabisChatbotFloatingButtonComponent, CustomResizeDirective]
})
export class SabisChatbotComponent implements OnInit, OnDestroy, AfterViewInit {
  public isChatbotAllowed: boolean = false;
  private _sub: Subscription = new Subscription();
  public isChatbotVisible: boolean = false;
  private _resizeObserver: MutationObserver | null = null;
  private _windowResizeListener: (() => void) | null = null;
  private _activeResizeDirective: CustomResizeDirective | null = null;
  public isSessionIdVisible: boolean = false;


  public chatbotConfig: ChatbotConfig = {
    userFirstName: this._userSession.getUser().firstName,
  };

  constructor(
    private _chatbotApiService: ChatbotApiService,
    private _chatbotPopupService: ChatbotPopupService,
    private _chatbotWindowSizeService: ChatbotWindowSizeService,
    private renderer: Renderer2,
    private el: ElementRef,
    private _userSession: UserSessionService,
    private _chatbotService: ChatbotService,
    @Inject('MessageService') private _messageService: IMessageService,
    private _inputService: InputService,
    private _chatbotTextTransferService: ChatbotTextTransferService,
    private _router: Router,
    private appConfig: AppConfigService
  ) {
    // Placed here to avoid circular dependency injection 
    this._userSession.sessionStateChanged().subscribe(isLoggedIn => {
      if (!isLoggedIn) {
        // Clear chatbot messages on logout
        this._messageService.clearMessages();
        this._inputService.clearPendingAppends();
        this._inputService.clearStoredMessage();
        this._inputService.setIsEditingMessage(false);
        this._inputService.setEditingMessageId(null);
        this._inputService.clearSelectedChatbotModeCode();
        this._chatbotService.closeActiveSession();
      }
    });

    // Subscribe to text copied from player
    this._sub.add(
      this._chatbotTextTransferService.textFromPlayer$.subscribe(textPayload => {
        if (textPayload && textPayload.textToCopy) {
          this.handleTextFromPlayer(textPayload.textToCopy);
        }
      })
    );
  }

  // Subscribe to check if chatbot is allowed
  ngOnInit() {
    this._sub.add(this._chatbotApiService
      .checkChatbotAvailability()
      .subscribe((response) => {
        this.isChatbotAllowed = response;
      }));

    this.isSessionIdVisible = this.appConfig.config!.isSessionIdVisible;
  }

  ngAfterViewInit() {
    // Wait for the chatbot popup to be rendered and apply resize functionality
    this._resizeObserver = new MutationObserver(() => {
      this.applyCustomResize();
    });

    this._resizeObserver.observe(this.el.nativeElement, {
      childList: true,
      subtree: true
    });

    // Listen for window resize events
    this._windowResizeListener = this.renderer.listen('window', 'resize', () => {
      this.handleWindowResize();
    });

  }

  private handleTextFromPlayer(text: string): void {
    if (!text || !text.trim()) {
      return;
    }
    this._chatbotPopupService.openPopup(text);
  }

  private handleWindowResize(): void {
    // Only recalculate if chatbot is visible
    if (this.isChatbotVisible) {
      const popupElement = document.querySelector('.sabis-chatbot-popup') as HTMLElement;
      if (popupElement) {
        const chatbotWidth = popupElement.offsetWidth;
        const viewportWidth = window.innerWidth;
        const bookWidth = viewportWidth - chatbotWidth;

        // Update CSS variables
        document.documentElement.style.setProperty('--chatbot-width', `${chatbotWidth}px`);
        document.documentElement.style.setProperty('--book-width', `${(bookWidth / viewportWidth) * 100}%`);
        document.documentElement.style.setProperty('--content-width-px', `${bookWidth}px`);

        // Update container elements
        const mainContent = document.querySelector('.app-main-content') as HTMLElement;
        const header = document.querySelector('.app-header') as HTMLElement;

        if (mainContent) {
          mainContent.style.width = `${bookWidth}px`;
        }
        if (header) {
          header.style.width = `${bookWidth}px`;
        }

        // Update the chatbot popup service
        this._chatbotWindowSizeService.setChatbotWidth(chatbotWidth);
      }
    }
  }

  private applyCustomResize(): void {
    const popupElement = document.querySelector('.sabis-chatbot-popup') as HTMLElement;
    if (popupElement && !popupElement.hasAttribute('data-resize-applied')) {
      // Mark as processed
      popupElement.setAttribute('data-resize-applied', 'true');
      // Destroy any previously created directive instance before creating a new one
      if (this._activeResizeDirective) {
        this._activeResizeDirective.ngOnDestroy();
        this._activeResizeDirective = null;
      }
      // Apply custom resize handles
      const directive = new CustomResizeDirective(
        { nativeElement: popupElement } as ElementRef,
        this.renderer
      );

      // Set properties
      directive.resizeEdges = 'left';
      directive.minWidth = 300;
      directive.maxWidth = 800;

      directive.ngOnInit();
      this._activeResizeDirective = directive;
    }
  }

  onVisibleChatbotChange(isChatbotVisible: boolean) {
    this.isChatbotVisible = isChatbotVisible;
    this._chatbotWindowSizeService.setChatbotPopupSubject(isChatbotVisible);

    if (isChatbotVisible) {
      setTimeout(() => {
        this.applyCustomResize();
        const popupElement = document.querySelector('.sabis-chatbot-popup') as HTMLElement;
        if (popupElement) {
          // Re-enable pointer events when the popup opens
          popupElement.style.pointerEvents = 'auto';
          const width = popupElement.offsetWidth;
          this._chatbotWindowSizeService.setChatbotWidth(width);

          const viewportWidth = window.innerWidth;
          const bookWidth = viewportWidth - width;
          document.documentElement.style.setProperty('--book-width', `${(bookWidth / viewportWidth) * 100}%`);
        }
      }, 100);
    } else {
      // RESET everything when chatbot closes
      this._chatbotWindowSizeService.setChatbotWidth(0);

      // Reset CSS variables
      document.documentElement.style.setProperty('--chatbot-width', '0px');
      document.documentElement.style.setProperty('--book-width', '100%');
      document.documentElement.style.setProperty('--content-width-px', '100%');

      // Reset direct styles
      const mainContent = document.querySelector('.app-main-content') as HTMLElement;
      const header = document.querySelector('.app-header') as HTMLElement;

      if (mainContent) {
        mainContent.style.width = '100%';
      }
      if (header) {
        header.style.width = '100%';
      }
      // Disable pointer events on the hidden popup so it no longer blocks interactions
      const popupElement = document.querySelector('.sabis-chatbot-popup') as HTMLElement;
      if (popupElement) {
        popupElement.style.pointerEvents = 'none';
      }

      // Trigger layout recalculation
      window.dispatchEvent(new Event('resize'));
    }
  }

  public onChatbotContentClick(event: { href: string; category: string | null }): void {
    if (event.category === PageLinks.EbookPageLink) {
      const url = new URL(event.href, window.location.origin);
      const pathParts = url.pathname.split('/').filter(Boolean);

      const isbn = url.searchParams.get('isbn') ?? pathParts[pathParts.length - 3];
      const version = url.searchParams.get('version') ?? pathParts[pathParts.length - 2];
      const rawPage = url.searchParams.get('page') ?? pathParts[pathParts.length - 1];
      const parsedPage = rawPage?.includes('-') ? rawPage.substring(rawPage.indexOf('-') + 1) : rawPage;

      if (!isbn || !version || !parsedPage) {
        console.warn('Invalid ebook-page-link payload:', event.href);
        return;
      }

      this._router.navigateByUrl(`${AppRoutes.ebookLauncher}/${isbn}/${version}/${parsedPage}`);
    }

  }

  ngOnDestroy(): void {
    this._sub.unsubscribe();
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
    }

    // Clean up the window resize listener
    if (this._windowResizeListener) {
      this._windowResizeListener();
    }
    // Clean up the manually instantiated resize directive
    if (this._activeResizeDirective) {
      this._activeResizeDirective.ngOnDestroy();
      this._activeResizeDirective = null;
    }
  }
}
